import type { SingleDayHours } from 'core/types/lenderConfig'

import * as R from './remeda'

export const DAY_CODES: Array<string> = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const BACKEND_DAY_INDEX_BY_TOKEN = new Map<string, number>(
  DAY_CODES.flatMap((code, index) => {
    const threeLetterCode = code.toLowerCase()
    const twoLetterCode = threeLetterCode.slice(0, 2)
    return [
      [threeLetterCode, index],
      [twoLetterCode, index],
    ] as Array<[string, number]>
  }),
)

const BACKEND_TWENTY_FOUR_HOUR_TOKENS = new Set(['24hr', '24hrs', '24hours'])

const TIME_RE = /^(\d{1,2})(?::?(\d{2})(?::(\d{2}))?)?\s*(?:([ap])\.?m?\.?)?$/i

const readDay = (token: string): number | undefined => BACKEND_DAY_INDEX_BY_TOKEN.get(token.trim().toLowerCase())

type ClockTime = { hours: number; minutes: number }

const to24Hour = (hours: number, meridiem: string | undefined): number | undefined => {
  if (meridiem === undefined) return hours <= 23 ? hours : undefined
  if (hours < 1 || hours > 12) return undefined

  const isPm = meridiem.toLowerCase() === 'p'
  return (hours % 12) + (isPm ? 12 : 0)
}

const readClockTime = (token: string): ClockTime | undefined => {
  const match = TIME_RE.exec(token.trim())
  if (!match) return undefined

  const [, rawHours, rawMinutes = '0', rawSeconds = '0', meridiem] = match
  const hours = to24Hour(Number(rawHours), meridiem)
  const minutes = Number(rawMinutes)
  const isValidClockTime = hours !== undefined && minutes <= 59 && Number(rawSeconds) <= 59

  return isValidClockTime ? { hours, minutes } : undefined
}

const pad = (value: number): string => String(value).padStart(2, '0')

const readTime = (token: string): string | undefined => {
  const time = readClockTime(token)
  return time && `${pad(time.hours)}:${pad(time.minutes)}:00`
}

const writeTime = (time: string): string => {
  const clockTime = readClockTime(time)
  return clockTime ? `${pad(clockTime.hours)}:${pad(clockTime.minutes)}` : time
}

export type DayHours = Omit<SingleDayHours, 'day'>

const readTimeRange = (rangeToken: string): [string, string] | undefined => {
  const sides = rangeToken.split('-')
  if (sides.length !== 2) return undefined

  const from = readTime(sides[0])
  const to = readTime(sides[1])
  if (from === undefined || to === undefined) return undefined

  const backendRejectsNonForwardRange = to <= from
  return backendRejectsNonForwardRange ? undefined : [from, to]
}

const readHours = (hoursPart: string): DayHours | undefined => {
  const normalized = hoursPart.trim().toLowerCase()
  if (normalized === 'closed') return { closed: true }
  if (BACKEND_TWENTY_FOUR_HOUR_TOKENS.has(normalized.replaceAll(' ', ''))) return { '24hours': true }

  const hoursList: Array<[string, string]> = []

  for (const rangeToken of hoursPart.split(',')) {
    const range = readTimeRange(rangeToken)
    if (!range) return undefined
    hoursList.push(range)
  }

  return hoursList.length > 0 ? { hoursList } : undefined
}

const daysFromTo = (start: number, end: number): Array<number> =>
  start < end ? R.range(start, end + 1) : [...R.range(start, DAY_CODES.length), ...R.range(0, end + 1)]

const readDayRange = (dayPart: string): Array<number> | undefined => {
  const sides = dayPart.split('-')
  if (sides.length !== 2) return undefined

  const start = readDay(sides[0])
  const end = readDay(sides[1])
  if (start === undefined || end === undefined) return undefined

  const backendRejectsSameDayRange = start === end
  return backendRejectsSameDayRange ? undefined : daysFromTo(start, end)
}

const readDayList = (dayPart: string): Array<number> | undefined => {
  const indices: Array<number> = []

  for (const token of dayPart.split(',')) {
    const index = readDay(token)
    if (index === undefined) return undefined
    indices.push(index)
  }

  const backendRejectsRepeatedDay = new Set(indices).size !== indices.length
  return backendRejectsRepeatedDay ? undefined : indices
}

const readDays = (dayPart: string): Array<number> | undefined => {
  const trimmed = dayPart.trim()
  return trimmed.includes('-') ? readDayRange(trimmed) : readDayList(trimmed)
}

const splitClauseAsBackendDoes = (clause: string): { dayPart: string; hoursPart: string } | undefined => {
  const firstWhitespace = clause.search(/\s/)
  if (firstWhitespace < 0) return undefined

  return { dayPart: clause.slice(0, firstWhitespace), hoursPart: clause.slice(firstWhitespace + 1) }
}

export type ParsedBusinessHours = {
  days: Array<SingleDayHours>
  discarded: Array<string>
}

export const parseBusinessHours = (normal: string): ParsedBusinessHours => {
  const byIndex: Array<SingleDayHours | undefined> = DAY_CODES.map(() => undefined)
  const discarded: Array<string> = []

  const clauses = normal
    .split(';')
    .map((clause) => clause.trim())
    .filter(Boolean)

  for (const clause of clauses) {
    const parts = splitClauseAsBackendDoes(clause)
    const dayIndices = parts && readDays(parts.dayPart)
    const hours = parts && readHours(parts.hoursPart)
    const backendRejectsRepeatedDay = dayIndices?.some((index) => byIndex[index] !== undefined) ?? false

    if (!dayIndices || !hours || backendRejectsRepeatedDay) {
      discarded.push(clause)
      continue
    }

    for (const index of dayIndices) byIndex[index] = { day: DAY_CODES[index], ...hours }
  }

  return {
    days: DAY_CODES.map((day, index) => byIndex[index] ?? { day, closed: true }),
    discarded,
  }
}

export const parseBusinessHoursNormalString = (value: string): Array<SingleDayHours> | undefined => {
  const clauseCount = value.split(';').filter((clause) => clause.trim()).length
  const { days, discarded } = parseBusinessHours(value)
  const hasReadableClause = discarded.length < clauseCount

  return hasReadableClause ? days : undefined
}

export const getBusinessHoursNormalAsArray = (
  normal: Array<SingleDayHours> | string | undefined,
): Array<SingleDayHours> | undefined => {
  if (normal === undefined) return undefined
  return parseBusinessHoursNormalString(Array.isArray(normal) ? formatBusinessHours(normal) : normal)
}

export const parseHoursToken = (hours: string): DayHours | undefined => readHours(hours)

const writeHours = (day: SingleDayHours | undefined): string => {
  const ranges = day?.hoursList ?? []
  const isOpen = day !== undefined && !day.closed

  if (isOpen && day['24hours']) return '24hours'
  if (!isOpen || ranges.length === 0) return 'Closed'

  return ranges.map(([from, to]) => `${writeTime(from)}-${writeTime(to)}`).join(',')
}

export const formatHoursToken = (hours: DayHours): string => writeHours({ day: 'MON', ...hours })

const writeDayPart = (start: number, end: number): string =>
  start === end ? DAY_CODES[start] : `${DAY_CODES[start]}-${DAY_CODES[end]}`

export const formatBusinessHours = (days: Array<SingleDayHours>): string => {
  const byCode = new Map(days.map((day) => [day.day.toUpperCase(), day]))
  const hours = DAY_CODES.map((code) => writeHours(byCode.get(code)))

  const clauses: Array<string> = []
  let runStart = 0

  for (let index = 1; index <= DAY_CODES.length; index++) {
    const continuesRun = index < DAY_CODES.length && hours[index] === hours[runStart]
    if (continuesRun) continue

    clauses.push(`${writeDayPart(runStart, index - 1)} ${hours[runStart]}`)
    runStart = index
  }

  return clauses.join('; ')
}
